export type NotificationEditorType = 'NOTIFICATION_ENGINE_TEMPLATE' | 'STATIC';

export interface NotificationLocaleModel {
  title?: string | null;
  subject?: string | null;
  emailBody?: string | null;
  smsBody?: string | null;
  emailTemplateName?: string | null;
  smsTemplateName?: string | null;
}

export interface NotificationEditorModel {
  notificationType: NotificationEditorType;
  locales: Record<string, NotificationLocaleModel>;
}

export interface NotificationVariable {
  group: string;
  label: string;
  value: string;
}

const SYSTEM_NOTIFICATION_VARIABLE_KEYS = [
  'activeFeedback',
  'applicantId',
  'applicantName',
  'applicantType',
  'applicationChannel',
  'applicationEvent',
  'applicationId',
  'applicationNumber',
  'applicationState',
  'approve_date',
  'approve_user',
  'billReferenceNumber',
  'categoryId',
  'categoryName',
  'certificate_expiration',
  'changeRequestId',
  'creatorType',
  'currency',
  'dateCreated',
  'emailNotificationRecipient',
  'feedbackDate',
  'feedbackSection',
  'feedbackTime',
  'formattedPrice',
  'lastUpdated',
  'locale',
  'officeId',
  'officeName',
  'officerState',
  'organizationAcronym',
  'organizationId',
  'paymentExpiration',
  'paymentExpirationTime',
  'paymentMethod',
  'paymentMerchant',
  'paymentStatus',
  'price',
  'priceType',
  'processingDeadline',
  'serviceId',
  'serviceName',
  'servicePaymentCode',
  'sourcePortal',
  'transactionId',
  'transactionTime',
  'userFullName',
  'userProvidingFeedback',
  'version',
];

export const DEFAULT_NOTIFICATION_VARIABLES: NotificationVariable[] =
  SYSTEM_NOTIFICATION_VARIABLE_KEYS.map(key => ({
    group: 'GLOBAL variables',
    label: key,
    value: '${' + key + '!}',
  }));

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const clone = <T>(value: T): T =>
  value == null ? value : JSON.parse(JSON.stringify(value));

export const readNotificationEditorModel = (
  args: Record<string, unknown> | null | undefined,
  locales: string[]
): NotificationEditorModel => {
  const source = args || {};
  const notificationType =
    source['notificationType'] === 'STATIC'
      ? 'STATIC'
      : 'NOTIFICATION_ENGINE_TEMPLATE';
  const sourceKey =
    notificationType === 'STATIC' ? 'staticTemplate' : 'dynamicTemplate';
  const templates = isRecord(source[sourceKey])
    ? (source[sourceKey] as Record<string, NotificationLocaleModel>)
    : {};
  const model: NotificationEditorModel = {
    notificationType,
    locales: clone(templates),
  };

  const existingLocales = Object.keys(model.locales);
  const shouldSeedLocales =
    !existingLocales.length ||
    existingLocales.some(locale => locales.includes(locale));
  if (shouldSeedLocales) {
    locales.forEach(locale => {
      if (!model.locales[locale]) model.locales[locale] = {};
    });
  }

  return model;
};

export const buildNotificationArgs = (
  model: NotificationEditorModel
): Record<string, unknown> => {
  if (model.notificationType === 'STATIC') {
    const staticTemplate: Record<string, unknown> = {};
    Object.entries(model.locales).forEach(([locale, value]) => {
      const next = {
        title: value.title || null,
        subject: value.subject || '',
        emailBody: value.emailBody || '',
        smsBody: value.smsBody || '',
      };
      if (Object.values(next).some(item => item !== null && item !== '')) {
        staticTemplate[locale] = next;
      }
    });

    return {
      notificationType: 'STATIC',
      staticTemplate,
    };
  }

  const dynamicTemplate: Record<string, unknown> = {};
  Object.entries(model.locales).forEach(([locale, value]) => {
    const next = {
      emailTemplateName: value.emailTemplateName || null,
      smsTemplateName: value.smsTemplateName || null,
    };
    if (Object.values(next).some(item => item !== null)) {
      dynamicTemplate[locale] = next;
    }
  });

  return {
    notificationType: 'NOTIFICATION_ENGINE_TEMPLATE',
    dynamicTemplate,
  };
};

export const extractNotificationVariables = (
  formFields: any
): NotificationVariable[] => {
  const formVariables: NotificationVariable[] = [];
  const seenFormKeys = new Set<string>();
  const addFormVariable = (key: string, label?: string): void => {
    if (!key || seenFormKeys.has(key)) return;
    seenFormKeys.add(key);
    formVariables.push({
      group: 'FORM Values',
      label: label || key,
      value: '${' + key + '!}',
    });
  };

  const visit = (node: any): void => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (!isRecord(node)) return;

    if (node['type'] === 'customcascadingdropdowns') {
      const configs = (node['props'] as any)?.configs;
      if (Array.isArray(configs)) {
        configs.forEach(config => {
          if (!config?.key) return;
          addFormVariable(config.key, config.label || config.key);
        });
      }
    } else if (node['key'] && node['type'] !== 'customfileupload') {
      addFormVariable(
        String(node['key']),
        (node['props'] as any)?.label || String(node['key'])
      );
    }

    visit(node['fieldGroup']);
  };

  visit(formFields?.fields);
  return [...formVariables, ...DEFAULT_NOTIFICATION_VARIABLES];
};
